import mediaTypeAdapter from '../../../../utils/mediaTypeAdapter';
import checkIsPeerIdRemotePeerId from '../../../../utils/checkIsPeerIdRemotePeerId';
import scalabilityLayers from '../../../../constants/scalabilityLayers';
import { mediaTypes } from '../../../../constants/mediaTypes';
import userStartedStreamEventAdapter from '../adapters/userStartedStreamEventAdapter';
import { isProducerActual, markProducerStarted } from './streamLifecycleGuard';

const CONSUME_QUEUE_KEY = '__global_consume_queue__';
const pendingConsumeProducerIds = new Set();

const delay = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

function userStartedStream(action, payload) {
  let adaptedPayload = { ...payload };

  if (!adaptedPayload.adapted) {
    adaptedPayload = userStartedStreamEventAdapter(payload);
  }

  const {
    peerId,
    producer,
    name,
    userId,
    isTeacher: isTeacherFromPayload,
    is_teacher: isTeacherSnakeCase,
  } = adaptedPayload;

  const producerId = producer?.producerId;

  if (!this.isReadyToHandleEvents) {
    const event = {
      eventName: action,
      pairID: producer?.streamType,
      eventData: { action, payload: adaptedPayload },
      peerID: peerId,
    };

    this.socket.rawEvents.push(event);
    return;
  }

  const isRemotePeerId = checkIsPeerIdRemotePeerId(this.peerInfo, peerId);

  if (!isRemotePeerId) {
    return;
  }

  if (!producerId) {
    console.warn('userStartedStream skipped: producerId is missing', {
      action,
      payload: adaptedPayload,
    });

    return;
  }

  const getPayloadSaysTeacher = () =>
    isTeacherFromPayload === true ||
    isTeacherFromPayload === 'true' ||
    isTeacherSnakeCase === true ||
    isTeacherSnakeCase === 'true';

  const getPeerRole = () => this.peerRoles?.get(peerId);

  const getIsTeacherPeer = () => {
    const payloadSaysTeacher = getPayloadSaysTeacher();
    const peerRole = getPeerRole();
    const roleSaysTeacher = peerRole?.isTeacher === true;

    if ((payloadSaysTeacher || roleSaysTeacher) && this.teacherPeerId !== peerId) {
      this.teacherPeerId = peerId;
    }

    return this.teacherPeerId === peerId || payloadSaysTeacher || roleSaysTeacher;
  };

  const waitForPeerRole = async () => {
    if (getIsTeacherPeer()) {
      return true;
    }

    for (let attempt = 0; attempt < 10; attempt += 1) {
      await delay(50);

      if (getIsTeacherPeer()) {
        return true;
      }

      if (getPeerRole()) {
        return false;
      }
    }

    return getIsTeacherPeer();
  };

  const { roomId } = this.roomInfo;
  const mediaType = mediaTypeAdapter(producer.streamType);

  markProducerStarted(peerId, mediaType, producerId);

  if (pendingConsumeProducerIds.has(producerId)) {
    console.warn('userStartedStream skipped: duplicate consume prevented', {
      peerId,
      producerId,
      mediaType,
      isTeacher: getIsTeacherPeer(),
    });

    return;
  }

  const producerInfo = {
    producerId,
    mediaType,
    peerName: name,
    userId,
  };

  const isConsumeQueueExist = this.queue.tasks.has(CONSUME_QUEUE_KEY);

  pendingConsumeProducerIds.add(producerId);

  this.queue.pushTask(CONSUME_QUEUE_KEY, async () => {
    let scalabilityLevel;

    const isTeacher = await waitForPeerRole();

    if (!isProducerActual(peerId, mediaType, producerId)) {
      pendingConsumeProducerIds.delete(producerId);
      this.queue.resumeTaskExecuting(CONSUME_QUEUE_KEY);

      console.warn('userStartedStream skipped: stale producer before consume', {
        peerId,
        producerId,
        mediaType,
        isTeacher,
      });

      return;
    }

    if (mediaType === mediaTypes.videoType || mediaType === mediaTypes.screenType) {
      scalabilityLevel = this.isAllClassMode ? scalabilityLayers.low : scalabilityLayers.high;
    }

    try {
      const { selectedMediaPeerId } = this.mediasoup.mediaStore;

      const shouldForceConsumeLargeVideo =
        !this.isAllClassMode &&
        mediaType === mediaTypes.videoType &&
        selectedMediaPeerId === peerId;

      const ignoreStreamsBlocking = isTeacher || shouldForceConsumeLargeVideo;

      console.log('userStartedStream before consume', {
        peerId,
        producerId,
        mediaType,
        isTeacher,
        teacherPeerId: this.teacherPeerId,
        peerRole: getPeerRole(),
        shouldForceConsumeLargeVideo,
        ignoreStreamsBlocking,
      });

      await this.mediasoup.consume(
        roomId,
        producerInfo,
        peerId,
        isTeacher,
        scalabilityLevel,
        ignoreStreamsBlocking,
      );
    } catch (error) {
      console.error('userStartedStream consume failed', {
        peerId,
        producerId,
        mediaType,
        isTeacher,
        error,
      });
    } finally {
      pendingConsumeProducerIds.delete(producerId);
      this.queue.resumeTaskExecuting(CONSUME_QUEUE_KEY);
    }
  });

  if (!isConsumeQueueExist) {
    this.queue.startTask(CONSUME_QUEUE_KEY);
  }
}

export default userStartedStream;
